import { useEffect, useState, useCallback } from "react";
import api, { apiError, formatDateTime, ROLE_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Loader2, ScrollText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ACTION_STYLES = {
  INSERT: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  UPDATE: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  DELETE: "border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400",
};

function DiffRow({ field, oldV, newV }) {
  const fmt = (v) => (v === null || v === undefined ? <span className="text-muted-foreground">—</span> : typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <tr className="border-b border-border/50 last:border-0">
      <td className="px-3 py-1.5 font-mono text-[11px] font-medium">{field}</td>
      <td className="max-w-[280px] break-words px-3 py-1.5 font-mono text-[11px] text-red-600 dark:text-red-400">{fmt(oldV)}</td>
      <td className="max-w-[280px] break-words px-3 py-1.5 font-mono text-[11px] text-emerald-600 dark:text-emerald-400">{fmt(newV)}</td>
    </tr>
  );
}

export default function AuditTrail() {
  const [data, setData] = useState({ total: 0, logs: [] });
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (entity !== "all") params.entity = entity;
      if (action !== "all") params.action = action;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/audit-logs", { params });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [entity, action, search, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-5" data-testid="audit-trail-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
          Audit Trail
          <span className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <ShieldCheck className="h-3 w-3 text-emerald-500" /> ISO 17025 §7.11
          </span>
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Immutable record of every insert, update and delete. {data.total} events.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Entity</Label>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-40" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              {["customer", "quotation", "equipment", "user"].map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-36" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {["INSERT", "UPDATE", "DELETE"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Search</Label>
          <Input className="w-48" placeholder="User or record ID…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search-input" />
        </div>
      </div>

      <div className="rounded-md border border-border" data-testid="audit-logs-list">
        {loading && <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>}
        {!loading && data.logs.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-sm text-muted-foreground">
            <ScrollText className="h-6 w-6" /> No audit events match the filters.
          </div>
        )}
        {!loading && data.logs.map((log) => (
          <div key={log.id} className="border-b border-border last:border-0" data-testid={`audit-entry-${log.id}`}>
            <button
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-accent/50"
              onClick={() => setExpanded(expanded === log.id ? null : log.id)}
              data-testid={`audit-expand-${log.id}`}
            >
              {expanded === log.id ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className={`rounded-sm border px-1.5 py-0.5 font-mono text-[10px] font-semibold ${ACTION_STYLES[log.action]}`}>{log.action}</span>
              <span className="text-sm font-medium capitalize">{log.entity}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{log.entity_id.slice(0, 8)}…</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {log.user_name} · <span className="font-mono text-[10px] uppercase">{ROLE_LABELS[log.user_role] || log.user_role}</span>
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">{formatDateTime(log.timestamp)}</span>
            </button>
            {expanded === log.id && (
              <div className="border-t border-border bg-muted/30 px-4 py-3" data-testid={`audit-diff-${log.id}`}>
                {log.changed_fields.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="px-3 py-1.5">Field</th>
                        <th className="px-3 py-1.5">Old Value</th>
                        <th className="px-3 py-1.5">New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {log.changed_fields.filter((f) => !["revisions", "email_log", "updated_at"].includes(f)).slice(0, 15).map((f) => (
                        <DiffRow key={f} field={f} oldV={log.old_value?.[f]} newV={log.new_value?.[f]} />
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-xs text-muted-foreground">No field-level changes recorded.</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
